import { Acabados } from '@types'
import { toVector3 } from '@utils/conversion/toVector3'
import { Box3, Material, Mesh, Object3D, Vector3 } from 'three'

const ZOCALO_NAME_PATTERN = /zocalo/i
const LATERAL_CATALOG_PREFIX = /^(LA|LB|LC|CC|CB|RA|RB|RC)/i

function findAcabadoForMaterial(materialName: string, acabados: Acabados[]): Acabados | undefined {
  let acabado = acabados.find((a) => a.zona === materialName)
  if (!acabado) {
    acabado = acabados.find((a) => {
      const cleanMaterialName = materialName.replace(/[0-9]/g, '').trim()
      const cleanZonaName = (a.zona || '').replace(/[0-9]/g, '').trim()
      return cleanMaterialName.includes(cleanZonaName) || cleanZonaName.includes(cleanMaterialName)
    })
  }
  return acabado
}

function getMeshMaterialName(mesh: Mesh): string {
  const mat = mesh.material
  if (Array.isArray(mat)) return (mat[0] as Material)?.name ?? ''
  return (mat as Material)?.name ?? ''
}

function boxSizeFromMeshes(root: Object3D, shouldInclude: (mesh: Mesh) => boolean): Vector3 | null {
  const box = new Box3()
  let hasMesh = false
  root.traverse((obj) => {
    if (!(obj as Mesh).isMesh) return
    const mesh = obj as Mesh
    if (!shouldInclude(mesh)) return
    hasMesh = true
    box.expandByObject(mesh)
  })
  if (!hasMesh || box.isEmpty()) return null
  return box.getSize(toVector3())
}

export function getGLBSize(glb: Record<string, any>) {
  const boundingBox = new Box3().setFromObject(glb as Object3D)
  return boundingBox.getSize(toVector3())
}

export function getGLBSizeExcludingMeshNames(
  root: Object3D,
  excludePattern: RegExp = ZOCALO_NAME_PATTERN,
): Vector3 | null {
  return boxSizeFromMeshes(root, (mesh) => !excludePattern.test(mesh.name))
}

export function getGLBSizeExcludingAcabadoZones(
  root: Object3D,
  acabados: Acabados[],
  excludeZones: string[],
): Vector3 | null {
  const exclude = new Set(excludeZones.map((z) => z.toLowerCase()))
  return boxSizeFromMeshes(root, (mesh) => {
    const acabado = findAcabadoForMaterial(getMeshMaterialName(mesh), acabados)
    if (!acabado) return true
    const zone = (acabado.zona || acabado.descripcion || '').toLowerCase()
    return !exclude.has(zone)
  })
}

export function getGLBSizeIncludingAcabadoZones(
  root: Object3D,
  acabados: Acabados[],
  includeZones: string[],
): Vector3 | null {
  const include = new Set(includeZones.map((z) => z.toLowerCase()))
  return boxSizeFromMeshes(root, (mesh) => {
    const acabado = findAcabadoForMaterial(getMeshMaterialName(mesh), acabados)
    if (!acabado) return false
    const zone = (acabado.zona || acabado.descripcion || '').toLowerCase()
    return include.has(zone)
  })
}

/** Base de escala: mide Costado (laterales) o excluye Zócalo, no bbox completo con zócalo. */
export function getGLBSizeForFreeDimension(
  fullRoot: Object3D,
  fullSize: Vector3,
  acabados?: Acabados[],
  catalogID?: string | number,
): Vector3 {
  const code = catalogID != null ? String(catalogID).trim().toUpperCase() : ''
  const isLateral = LATERAL_CATALOG_PREFIX.test(code)

  if (acabados?.length && isLateral) {
    const costadoOnly = getGLBSizeIncludingAcabadoZones(fullRoot, acabados, ['Costado'])
    if (costadoOnly) return costadoOnly
  }

  if (acabados?.length) {
    const withoutZocaloZone = getGLBSizeExcludingAcabadoZones(fullRoot, acabados, ['Zocalo'])
    if (withoutZocaloZone) return withoutZocaloZone
  }

  const withoutZocaloName = getGLBSizeExcludingMeshNames(fullRoot)
  if (withoutZocaloName) return withoutZocaloName

  return fullSize
}
